<template>
  <div id="edit_attendance_admin" class="modal fade" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">Edit Attendance</h4>
          <button
            type="button"
            class="btn-close custom-btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="onSubmit">
          <div class="modal-body pb-0">
            <div v-if="!recordId" class="alert alert-warning">
              Select an attendance record to edit.
            </div>

            <template v-else>
              <div class="row mb-3">
                <div class="col-md-6">
                  <label class="form-label text-muted">Employee</label>
                  <p class="fw-medium mb-0">{{ employeeName }}</p>
                </div>
                <div class="col-md-6">
                  <label class="form-label text-muted">Department</label>
                  <p class="fw-medium mb-0">{{ departmentName }}</p>
                </div>
              </div>

              <div class="row g-3">
                <div class="col-md-4">
                  <label class="form-label">Date</label>
                  <input
                    type="date"
                    class="form-control"
                    :value="form.work_date"
                    disabled
                  />
                </div>

                <div class="col-md-4">
                  <label class="form-label">Status</label>
                  <select v-model="form.status" class="form-select" required>
                    <option value="Present">Present</option>
                    <option value="Absent">Absent</option>
                  </select>
                </div>

                <div class="col-md-4">
                  <label class="form-label">Break (minutes)</label>
                  <input
                    v-model.number="form.break_minutes"
                    type="number"
                    min="0"
                    max="720"
                    class="form-control"
                    :disabled="form.status === 'Absent'"
                  />
                </div>

                <div class="col-md-6">
                  <label class="form-label">Check In</label>
                  <input
                    v-model="form.check_in_time"
                    type="time"
                    class="form-control"
                    :disabled="form.status === 'Absent'"
                  />
                </div>

                <div class="col-md-6">
                  <label class="form-label">Check Out</label>
                  <input
                    v-model="form.check_out_time"
                    type="time"
                    class="form-control"
                    :disabled="form.status === 'Absent'"
                  />
                </div>

                <div class="col-md-12">
                  <label class="form-label">Notes</label>
                  <textarea
                    v-model="form.notes"
                    class="form-control"
                    rows="2"
                    maxlength="1000"
                    placeholder="Optional admin note"
                  />
                </div>

                <div class="col-12" v-if="form.status === 'Present'">
                  <div class="alert alert-light border mb-0">
                    <div class="row text-center">
                      <div class="col-4">
                        <span class="text-muted d-block fs-12">Late</span>
                        <strong>{{ previewLate }}</strong>
                      </div>
                      <div class="col-4">
                        <span class="text-muted d-block fs-12">Production</span>
                        <strong>{{ previewProduction }}</strong>
                      </div>
                      <div class="col-4">
                        <span class="text-muted d-block fs-12">Overtime</span>
                        <strong>{{ previewOvertime }}</strong>
                      </div>
                    </div>
                    <p class="text-muted fs-12 mb-0 mt-2">
                      Minutes are recalculated on save from shift rules.
                    </p>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-light me-2"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="submitting || !recordId"
            >
              <span
                v-if="submitting"
                class="spinner-border spinner-border-sm me-1"
              ></span>
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <div id="attendance_admin_delete_modal" class="modal fade" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-md">
      <div class="modal-content">
        <div class="modal-header border-0 pb-0">
          <div class="d-flex align-items-center gap-3">
            <span
              class="avatar avatar-md bg-danger-transparent text-danger rounded-circle d-inline-flex align-items-center justify-content-center"
            >
              <i class="ti ti-trash fs-18"></i>
            </span>
            <div>
              <h4 class="modal-title mb-0">Confirm Delete</h4>
              <p class="text-muted mb-0 fs-13">This action cannot be undone.</p>
            </div>
          </div>
          <button
            type="button"
            class="btn-close custom-btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <div class="modal-body pt-3">
          <div class="alert alert-danger d-flex align-items-start gap-2 mb-0">
            <i class="ti ti-alert-triangle mt-1"></i>
            <div>
              <div class="fw-medium">Delete attendance record?</div>
              <div class="fs-13">
                You are about to permanently remove
                <strong>{{ deleteEmployeeName }}</strong>'s attendance
                <span v-if="deleteDateLabel">on <strong>{{ deleteDateLabel }}</strong></span>.
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer border-0 pt-0">
          <button
            type="button"
            class="btn btn-light"
            data-bs-dismiss="modal"
            :disabled="deleting"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-danger"
            :disabled="deleting || !deleteRecordId"
            @click="onConfirmDelete"
          >
            <span
              v-if="deleting"
              class="spinner-border spinner-border-sm me-1"
            ></span>
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from "vue";
import Modal from "bootstrap/js/dist/modal";
import attendanceService from "@/services/hrm/attendanceEmployee.service";
import { formatAttendanceDate, parseAttendanceDateTime } from "@/utils/attendanceDateTime";
import { toast } from "@/utils/toast";

const props = defineProps({
  record: { type: Object, default: null },
  deleteRecord: { type: Object, default: null },
});

const emit = defineEmits(["saved", "deleted"]);

const submitting = ref(false);
const deleting = ref(false);

const form = reactive({
  work_date: "",
  status: "Present",
  check_in_time: "",
  check_out_time: "",
  break_minutes: 0,
  notes: "",
});

const recordId = computed(() => {
  const id = props.record?.id ?? props.record?.raw?.id;
  return Number.isFinite(Number(id)) && Number(id) > 0 ? Number(id) : null;
});

const employeeName = computed(
  () =>
    props.record?.employee_name ||
    props.record?.employee?.name ||
    props.record?.raw?.employee?.name ||
    "-"
);

const departmentName = computed(
  () =>
    props.record?.department ||
    props.record?.employee?.department ||
    props.record?.raw?.employee?.department ||
    "-"
);

const deleteRecordId = computed(() => {
  const id = props.deleteRecord?.id ?? props.deleteRecord?.raw?.id;
  return Number.isFinite(Number(id)) && Number(id) > 0 ? Number(id) : null;
});

const deleteEmployeeName = computed(
  () =>
    props.deleteRecord?.employee_name ||
    props.deleteRecord?.employee?.name ||
    props.deleteRecord?.raw?.employee?.name ||
    "this employee"
);

const deleteDateLabel = computed(() => {
  const raw = props.deleteRecord?.raw || props.deleteRecord || {};
  const date = raw.date || raw.work_date;
  return date ? formatAttendanceDate(date) : "";
});

function minutesToLabel(m) {
  const minutes = Math.max(0, parseInt(m || 0, 10));
  const h = Math.floor(minutes / 60);
  const mm = minutes % 60;
  return `${String(h).padStart(2, "0")}h ${String(mm).padStart(2, "0")}m`;
}

const previewLate = computed(() =>
  minutesToLabel(props.record?.late_minutes ?? props.record?.raw?.late_minutes)
);
const previewProduction = computed(() =>
  minutesToLabel(
    props.record?.total_work_minutes ?? props.record?.raw?.total_work_minutes
  )
);
const previewOvertime = computed(() =>
  minutesToLabel(
    props.record?.overtime_minutes ?? props.record?.raw?.overtime_minutes
  )
);

function timeInputValue(value) {
  const d = parseAttendanceDateTime(value);
  if (!d) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function hydrateForm(record) {
  const raw = record?.raw || record || {};
  form.work_date = raw.date || raw.work_date || "";
  form.status = raw.status === "Absent" ? "Absent" : "Present";
  form.check_in_time = timeInputValue(raw.check_in_at);
  form.check_out_time = timeInputValue(raw.check_out_at);
  form.break_minutes = Number(raw.break_minutes || 0);
  form.notes = raw.notes || "";
}

watch(
  () => props.record,
  (record) => {
    if (record) hydrateForm(record);
  },
  { immediate: true }
);

async function onSubmit() {
  if (!recordId.value) return;

  submitting.value = true;
  try {
    await attendanceService.update(recordId.value, {
      status: form.status,
      work_date: form.work_date || undefined,
      check_in_time: form.status === "Present" ? form.check_in_time || null : null,
      check_out_time: form.status === "Present" ? form.check_out_time || null : null,
      break_minutes: form.status === "Present" ? form.break_minutes : 0,
      notes: form.notes || null,
    });

    toast("Attendance updated", "success");
    Modal.getInstance(document.getElementById("edit_attendance_admin"))?.hide();
    emit("saved");
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to update attendance", "error");
  } finally {
    submitting.value = false;
  }
}

async function onConfirmDelete() {
  if (!deleteRecordId.value) return;

  deleting.value = true;
  try {
    await attendanceService.destroy(deleteRecordId.value);
    toast("Attendance deleted", "success");
    Modal.getInstance(
      document.getElementById("attendance_admin_delete_modal")
    )?.hide();
    emit("deleted");
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to delete attendance", "error");
  } finally {
    deleting.value = false;
  }
}
</script>
